// Der sichtbare Ausschnitt einer Liste, die VOLLSTÄNDIG im Speicher liegt.
//
// ── ⚠️ ZUERST DIE GRENZE ───────────────────────────────────────────────────────────────────────
// Das hier ist **Anzeige-Blättern, kein Nachladen**. Die drei Verbraucher (Alben, Interpreten,
// Playlists) holen ihre Liste weiterhin am Stück, und das ist keine Bequemlichkeit, sondern der
// gemessene Stand des Backends:
//
// | Liste       | Route                    | Handler                                     | nimmt page/limit | liefert pagination |
// |-------------|--------------------------|---------------------------------------------|------------------|--------------------|
// | Alben       | GET /api/admin/albums    | AlbumController.getAdminAlbums              | nein             | nein               |
// | Interpreten | GET /api/admin/artists   | ArtistController.getAdminArtists            | nein             | nein               |
// | Playlists   | GET /api/admin/playlists | AdminPlaylistController.listPublicPlaylists | nein             | nein               |
//
// Alle drei Handler nehmen das Anfrage-Objekt gar nicht erst entgegen — der Parameter heißt in
// allen dreien `_req` und wird nie gelesen. Die Dienste dahinter (`AlbumService.getAdminAlbums`,
// `ArtistService.getAdminArtists`, `PlaylistService.getAdminPlaylists`) fahren `findMany` ohne
// `skip`/`take` und antworten mit `res.json({ data })` — ohne den `pagination`-Umschlag, den die
// Audio-, Video-, Inhalte- und Benutzer-Listen mitbringen.
//
// **Was dieser Haken also NICHT tut:** die Leitung entlasten. Bei 500 Alben gehen weiterhin 500
// Zeilen über das Netz. Abgestellt ist die 500-zeilige TABELLE im Dokument und das Suchen von Hand.
// Wer auch die Leitung entlasten will, muss die drei Endpunkte um `page`/`limit` und den
// `pagination`-Umschlag erweitern; dann ersetzt das Muster der Audio-/Video-/Benutzer-Seiten
// (Filter → Anfrage → `response.pagination`) diesen Haken hier vollständig, und die Verbraucher
// verlieren genau die vier Zeilen, die ihn aufrufen.
//
// ── Warum überhaupt ein Haken und nicht dreimal fünfzehn Zeilen ────────────────────────────────
// Wegen des Rücksprungs. Er ist der einzige nicht offensichtliche Teil, und er ist genau die
// Sorte Bedingung, die man dreimal abschreibt und einmal falsch abschreibt — dieselbe Begründung,
// mit der `SeitenblaetterFuss` die Bedingung `totalPages > 1` eingesammelt hat.
//
// ⚠️ Und er sitzt hier ANDERS als bei der Benutzer-Verwaltung, aus einem belegbaren Grund.
// `UserManagement.ladeBenutzer` prüft `response.data.length === 0 && page > 1` am LADE-ERGEBNIS —
// dort muss sie das, weil der Server die einzige Instanz ist, die weiß, ob Seite 2 leer ist. Hier
// liegt die ganze Liste im Speicher: ob Seite 2 existiert, ist eine Rechnung, kein Messwert. Also
// wird geklemmt, wo gerechnet wird, und nicht in der Ladefunktion.
import { useEffect, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';

/**
 * Der Teil des Seiten-Zustands, den dieser Haken anfasst — genau eine Zahl.
 *
 * ⚠️ Das Feld heißt `page` und nicht `seite`, damit `seitenwechsel` aus `filterZustand.ts` darauf
 * passt. Der Rückruf für „Zurück"/„Weiter" ist damit bei allen acht blätterbaren Listen derselbe,
 * samt der Regel, die dort festgehalten ist: die Seitenzahl ist kein Filter.
 */
interface Blaetterstand {
  page: number;
}

/**
 * Schneidet `eintraege` auf die aktuelle Seite zu und hält die Seitenzahl im gültigen Bereich.
 *
 * Zurück kommt die GEKLEMMTE Seite, nicht die im Zustand stehende — beides zusammen ist der
 * Rücksprung:
 *  1. Die Klemmung wirkt beim Rechnen, also schon im ersten Bild. Ohne sie stünde nach dem Löschen
 *     der letzten Zeile von Seite 2 für einen Anzeigeschritt eine leere Tabelle da.
 *  2. Der Effekt schreibt den geklemmten Wert in den Zustand zurück. Ohne ihn bliebe dort still
 *     eine 2 stehen: die Nutzerin sähe Seite 1, und sobald die Liste wieder wüchse, spränge die
 *     Ansicht ohne Klick auf Seite 2.
 *
 * ⚠️ Der Effekt kann nicht schleifen. Er setzt ausschließlich auf den bereits berechneten Wert
 * `seite`, und die Funktionsform gibt `vorher` UNVERÄNDERT zurück, wenn dort schon dasselbe steht —
 * React bricht dann ab, ohne neu zu zeichnen. `setzeZustand` stammt aus `useState` und ist stabil,
 * der Effekt läuft also nicht bei jedem Zeichnen erneut.
 */
export function useSeitenausschnitt<T, F extends Blaetterstand>(
  eintraege: readonly T[],
  zeilenJeSeite: number,
  zustand: F,
  setzeZustand: Dispatch<SetStateAction<F>>,
) {
  const gesamt = eintraege.length;
  const seitenAnzahl = Math.ceil(gesamt / zeilenJeSeite);
  // `Math.max(1, seitenAnzahl)` fängt die leere Liste ab: `seitenAnzahl` ist dann 0, und eine obere
  // Grenze von 0 machte die Seite zu 0 statt zu 1. Der Blätter-Fuß rendert bei 0 ohnehin nichts,
  // aber der Zustand bliebe mit einer 0 zurück, aus der „Weiter" auf Seite 1 führte — ein Schritt,
  // der sich anfühlt, als wäre man vorher irgendwo gewesen.
  const seite = Math.min(Math.max(1, zustand.page), Math.max(1, seitenAnzahl));

  const ausschnitt = useMemo(
    () => eintraege.slice((seite - 1) * zeilenJeSeite, seite * zeilenJeSeite),
    [eintraege, seite, zeilenJeSeite],
  );

  useEffect(() => {
    setzeZustand((vorher) => (vorher.page === seite ? vorher : { ...vorher, page: seite }));
  }, [seite, setzeZustand]);

  return { ausschnitt, seite, seitenAnzahl, gesamt };
}
